import { format, isToday, isYesterday } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { createColumnHelper } from '@tanstack/react-table'
import clsx from 'clsx'

import { getNameInitials } from '@/helpers/getNameInitials'
import { FilterType, Log, RouteSelectionMode, UpdatedRecordsType } from '@/types/logs'
import { CommonHeader } from '@/ui/Table'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'
import { UPDATED_RECORDS_LABEL_MAP } from '@/pages/UnfinishedRecords/constant'
import { sortLogsBy } from '@/modules/CombinedLogs/helpers/sortLogsBy'
import { filterLogsBy } from '@/modules/CombinedLogs/helpers/filterLogsBy'
import { sortLogsByTime } from '@/modules/CombinedLogs/helpers/sortLogsByTime'
import { Trip } from '@/modules/CombinedLogs/ui/components/information/Trip'
import { DefaultValue } from '../ui/components/DefaultValue'
import { DeadlineHeader } from '../ui/components/performanceManager/DeadlineHeader'
import { useCombinedLogsTable } from '@/modules/CombinedLogs/hooks/useCombinedLogsTable'

import styles from '../ui/components/performanceManager/UnfinishedRecordsTable.module.scss'

import ChatIcon from '../assets/chat.svg'
import IndicatorIcon from '../assets/indicator.svg'
import { Code } from '@/modules/CombinedLogs/ui/components/information/Code'
import { useCallback, useMemo, useRef } from 'react'
import { useCachedGetCodeName } from '@/modules/CombinedLogs/hooks/useCachedGetCodeName'
import { PrimitiveDefaultValue } from '../ui/components/PrimitiveDefaultValue'
import { useManageLogOriginalRoute } from '@/modules/CombinedLogs/hooks/useManageLogOriginalRoute'
import { useBusStops } from './useBusStops'
import { formatCodeFilterValues } from '@/modules/CombinedLogs/helpers/formatCodeFilterValues'
import { useGetCodes } from '@/modules/CombinedLogs/api/useGetCodes'
import { LocationUnfinished } from '@/modules/CombinedLogs/ui/components/information/LocationUnfinished'
import { LinkToLog } from '../ui/components/LinkToLog'
import { AuthorTooltip } from '@/modules/CombinedLogs/ui/components/TableForm/tooltips/AuthorTooltip'
import { CommentIcon } from '@/modules/CombinedLogs/ui/components/information/CommentIcon'

const WORKSTATION_COLUMN = 'workstation'

const columnHelper = createColumnHelper<Log>()

export const useUnfinishedRecordsColumns = (isDataLoading: boolean, actionCallback?: (log: Log) => void) => {
    const formatFlagDate = (date: string): string => {
        if (isToday(date)) {
            return `${format(date, 'HH:mm')} today`
        }

        return format(date, 'HH:mm dd/MM/yyyy')
    }

    const { setRowSelection } = useCombinedLogsTable()

    const emptyCodesRef = useRef([])
    const { data: codes = emptyCodesRef.current } = useGetCodes()
    const getCodeName = useCachedGetCodeName()

    const { getElementAttributes } = useManageLogOriginalRoute(isDataLoading)

    const { getRouteBusStops, allStops: allStopsData } = useBusStops()

    const nameInitialsCache = useRef(new Map<string, string>())
    const getNameInitialsMemoized = useCallback((value: string) => {
        if (nameInitialsCache.current.has(value)) {
            return nameInitialsCache.current.get(value)
        }

        const initials = getNameInitials(value)
        nameInitialsCache.current.set(value, initials)

        return initials
    }, [])

    const columns = useMemo(() => {
        const handleArrowClick = (log: Log) => {
            setRowSelection({ [log.id]: true })
            actionCallback && actionCallback(log)
        }
        return [
            columnHelper.accessor('code', {
                id: COMBINED_LOGS_COLUMNS.Code,
                header: (context) => (
                    <CommonHeader
                        label="Code"
                        buttonLabel="Sort by code"
                        searchPlaceholder="Type to search for code"
                        {...context}
                    />
                ),
                cell: (info) => <Code {...info} getCodeName={getCodeName} />,
                filterFn: filterLogsBy((log) => !log.code),
                meta: {
                    isPlaceholder: true,
                    placeholder: 'Code',
                    alignCenter: true,
                    width: 'var(--size-128)',
                    formatFilterValues: (values) => formatCodeFilterValues(values, FilterType.All, codes),
                },
                enableSorting: true,
            }),
            columnHelper.accessor('reference', {
                id: COMBINED_LOGS_COLUMNS.Reference,
                header: 'Reference',
                cell: (info) => <DefaultValue {...info} />,
                enableSorting: false,
                meta: {
                    alignCenter: true,
                    width: '7.142rem',
                },
            }),
            columnHelper.accessor((row) => row.routes, {
                id: COMBINED_LOGS_COLUMNS.Routes,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by route"
                        searchPlaceholder="Type to search for route"
                        label="Route"
                    />
                ),
                cell: (info) => {
                    return info.row.original.routeSelectionMode === RouteSelectionMode.None ? (
                        <>-</>
                    ) : (
                        <PrimitiveDefaultValue
                            value={info.getValue()}
                            updated={info.row.original.propertiesChanged?.includes(info.column.id)}
                        />
                    )
                },
                meta: {
                    alignCenter: true,
                    width: '7.142rem',
                },
                filterFn: filterLogsBy((log) => log.routeSelectionMode === RouteSelectionMode.None),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Routes),
            }),
            columnHelper.accessor('trip', {
                id: COMBINED_LOGS_COLUMNS.Trip,
                header: 'Trip',
                cell: (info) => (
                    <Trip {...info} isDataLoading={isDataLoading} getElementAttributes={getElementAttributes} />
                ),
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('run', {
                id: COMBINED_LOGS_COLUMNS.Run,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by run"
                        searchPlaceholder="Type to search for run"
                        label="Run"
                    />
                ),
                cell: (info) => (
                    <PrimitiveDefaultValue
                        value={info.getValue()}
                        updated={info.row.original.propertiesChanged?.includes(info.column.id)}
                    />
                ),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Run, (log) => !log.run),
                filterFn: filterLogsBy((log) => !!log.run),
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('duty', {
                id: COMBINED_LOGS_COLUMNS.Duty,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by duty"
                        searchPlaceholder="Type to search for duty"
                        label="Duty"
                    />
                ),
                cell: (info) => (
                    <PrimitiveDefaultValue
                        value={info.getValue()}
                        updated={info.row.original.propertiesChanged?.includes(info.column.id)}
                    />
                ),
                filterFn: filterLogsBy((log) => !log.duty.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Duty, (log) => !log.duty?.length),
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('fleet', {
                id: COMBINED_LOGS_COLUMNS.Fleet,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by fleet"
                        searchPlaceholder="Type to search for fleet"
                        label="Fleet"
                    />
                ),
                cell: (info) => (
                    <PrimitiveDefaultValue
                        value={info.getValue()}
                        updated={info.row.original.propertiesChanged?.includes(info.column.id)}
                    />
                ),
                filterFn: filterLogsBy((log) => !log.fleet?.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Fleet, (log) => !log.fleet?.length),
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor(
                (row) => ({
                    locationFrom: row.locationFrom,
                    locationTo: row.locationTo,
                    locationType: row.locationType,
                    direction: row.direction,
                }),
                {
                    id: COMBINED_LOGS_COLUMNS.Location,
                    header: 'Location',
                    cell: (info) => (
                        <LocationUnfinished {...info} getRouteBusStops={getRouteBusStops} allStopsData={allStopsData} />
                    ),
                    meta: {
                        width: '100%',
                    },
                }
            ),
            columnHelper.accessor((row) => row.descriptionInfo.description, {
                id: COMBINED_LOGS_COLUMNS.Description,
                header: 'Description',
                cell: (info) => (
                    <PrimitiveDefaultValue
                        value={info.getValue()}
                        updated={info.row.original.propertiesChanged?.includes(info.column.id)}
                    />
                ),
                meta: {
                    width: '100%',
                },
            }),
            columnHelper.accessor('delayInMinutes', {
                id: COMBINED_LOGS_COLUMNS.Delay,
                header: 'Delay',
                cell: (info) => {
                    const value = info.getValue()

                    if (!value) {
                        return '-'
                    }

                    return <span>{`${value} ${!isNaN(value) ? 'min' : ''}`}</span>
                },
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('mileage', {
                id: COMBINED_LOGS_COLUMNS.Milage,
                header: 'Mileage',
                cell: (info) => (
                    <PrimitiveDefaultValue
                        value={info.getValue()}
                        updated={info.row.original.propertiesChanged?.includes(info.column.id)}
                    />
                ),
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('workstation', {
                id: WORKSTATION_COLUMN,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by workstation"
                        searchPlaceholder="Type to search for workstation"
                        label="Workstn"
                    />
                ),
                cell: (info) => (
                    <PrimitiveDefaultValue
                        value={info.getValue()}
                        updated={info.row.original.propertiesChanged?.includes(info.column.id)}
                    />
                ),
                meta: {
                    alignCenter: true,
                    width: '6.428rem',
                },
            }),
            columnHelper.accessor('author', {
                id: COMBINED_LOGS_COLUMNS.Author,
                header: 'Author',
                cell: (info) => (
                    <AuthorTooltip author={info.getValue() || '-'}>
                        <span className={styles.greyCell}>
                            {info.getValue() ? getNameInitialsMemoized(info.getValue()) : '-'}
                        </span>
                    </AuthorTooltip>
                ),
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('timestamp', {
                id: COMBINED_LOGS_COLUMNS.Time,
                header: (info) => <DeadlineHeader {...info} />,
                cell: (info) => {
                    const formattedTime = formatInTimeZone(info.getValue(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss")
                    const showAsToday = isToday(formattedTime) || isYesterday(formattedTime)

                    return (
                        <div className={clsx({ [styles.overdue]: !showAsToday, [styles.today]: showAsToday })}>
                            <span>{showAsToday ? 'Today' : 'Overdue'}</span>
                            {info.row.original.isUpdatedAfterUnfinished && <IndicatorIcon />}
                        </div>
                    )
                },
                sortingFn: (rowA, rowB) => sortLogsByTime(rowA.original, rowB.original),
                meta: {
                    alignCenter: true,
                    width: '7.142rem',
                },
            }),
            columnHelper.display({
                id: COMBINED_LOGS_COLUMNS.Actions,
                cell: (info) => {
                    const log = info.row.original
                    const isSelected = info.row.getIsSelected()

                    if (!log.unfinishedType) {
                        return <></>
                    }

                    return (
                        <>
                            <div className={styles.updatedTypeContainer}>
                                {UPDATED_RECORDS_LABEL_MAP[log.unfinishedType]}
                                {info.row.original.unfinishedType === UpdatedRecordsType.Flagged && <ChatIcon />}
                            </div>
                            {log.unfinishedType === UpdatedRecordsType.Flagged &&
                                log.unfinishedFlagMetadata &&
                                isSelected && (
                                    <div className={styles.flagInformationWrapper}>
                                        <ChatIcon />
                                        <div className={styles.flagInformation}>
                                            <span className={styles.flagDescription}>
                                                {log.unfinishedFlagMetadata.comment}
                                            </span>
                                            <div className={styles.flagFooter}>
                                                <span className={styles.flagAuthor}>
                                                    {log.unfinishedFlagMetadata.author}
                                                </span>
                                                <span className={styles.flagTime}>
                                                    {formatFlagDate(log.unfinishedFlagMetadata.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </>
                    )
                },
                meta: {
                    width: '12.714rem',
                    alignCenter: true,
                },
            }),
            columnHelper.display({
                id: 'comments',
                header: () => null,
                cell: (info) => <CommentIcon log={info.row.original} />,
            }),
            ...(actionCallback
                ? [
                      columnHelper.display({
                          id: 'arrowLeft',
                          cell: (info) => <LinkToLog {...info} handleArrowClick={handleArrowClick} />,
                          meta: {
                              alignCenter: true,
                              width: '2.5rem',
                          },
                      }),
                  ]
                : []),
        ]
    }, [
        actionCallback,
        setRowSelection,
        getCodeName,
        isDataLoading,
        getElementAttributes,
        getRouteBusStops,
        allStopsData,
        getNameInitialsMemoized,
        codes,
    ])

    return columns
}
